import React from "react";
import {
  Box,
  Typography,
  TextField,
  TextFieldProps,
  TypographyProps,
  styled,
  Paper,
  keyframes,
} from "@mui/material";
import { Dashboard as DashboardIcon } from "@mui/icons-material";
// Animation keyframes
const slideIn = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// Styled components
const SettingCard = styled(Paper)(({ theme }) => ({
  animation: `${slideIn} 0.3s ease-out`,
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
  },
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

type SettingRow = {
  label: string;
  value: number;
  unit: string;
  key: string;
  inputProps?: TextFieldProps;
  typographyProps?: TypographyProps;
};

type ReusableSettingsCardProps = {
  icon?: React.ReactNode;
  title: string;
  settings: SettingRow[];
  onChange: (key: string, value: number) => void;
};

const ReusableSettingsCard = ({
  icon = <DashboardIcon />,
  title,
  settings,
  onChange,
}: ReusableSettingsCardProps) => {
  const handleChange =
    (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(key, parseInt(e.target.value, 10));
    };

  return (
    <Box>
      <SettingCard elevation={3}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          {React.cloneElement(icon as React.ReactElement, { sx: { mr: 1 } })}
          <Typography variant="h6">{title}</Typography>
        </Box>

        {settings.map((setting) => (
          <Box key={setting.key} sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="body1"
                sx={{ minWidth: 200 }}
                {...setting.typographyProps}
              >
                {setting.label}
              </Typography>
              <TextField
                type="number"
                value={setting.value}
                onChange={handleChange(setting.key)}
                sx={{ width: 150 }}
                variant="outlined"
                size="small"
                inputProps={{
                  min: 1,
                  max: 100,
                  ...setting.inputProps?.inputProps,
                }}
                {...setting.inputProps}
              />
              <Typography variant="body1">{setting.unit}</Typography>
            </Box>
          </Box>
        ))}
      </SettingCard>
    </Box>
  );
};

export default ReusableSettingsCard;
